import React, { useMemo, useState } from "react";
import { Button, Card, Col, Row, Space, Typography, message } from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";

import TransactionPlanFilters from "../../components/transactionPlan/TransactionPlanFilters";
import TransactionPlanTable from "../../components/transactionPlan/TransactionPlanTable";
import TransactionPlanInvoiceModal from "../../components/transactionPlan/TransactionPlanInvoiceModal";
import TransactionPlanAddModal from "../../components/transactionPlan/TransactionPlanAddModal";
import TransactionPlanEditModal from "../../components/transactionPlan/TransactionPlanEditModal";
import { getTransactionPlanAccess } from "../../components/transactionPlan/transactionPlanPermissions";
import useTransactionPlanList from "../../hooks/useTransactionPlanList";
import {
    deleteTransactionPlan,
    replaceTransactionPlanInvoice,
    uploadTransactionPlanInvoice,
} from "../../api/transactionPlanApi";
import { useTranslation } from "../../i18n/LanguageProvider";

const { Title } = Typography;

/**
 * Гүйлгээний төлөвлөгөө page: the module's single destination. Listing, searching, filtering,
 * sorting, invoices and - for the add role - Excel import and manual entry all live here.
 */
function TransactionPlanListPage() {
    const { t } = useTranslation();
    const access = useMemo(() => getTransactionPlanAccess(), []);

    const {
        rows,
        loading,
        error,
        page,
        pageSize,
        total,
        search,
        setSearch,
        columnFilters,
        executionDateRange,
        setExecutionDateRange,
        filterOptions,
        handleTableChange,
        clearFilters,
        reload,
    } = useTransactionPlanList();

    const [invoiceRecord, setInvoiceRecord] = useState(null);
    const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editRecord, setEditRecord] = useState(null);
    const [editModalOpen, setEditModalOpen] = useState(false);

    const openInvoiceModal = (record) => {
        setInvoiceRecord(record);
        setInvoiceModalOpen(true);
    };

    const closeInvoiceModal = () => {
        setInvoiceModalOpen(false);
        setInvoiceRecord(null);
    };

    const handleUploadInvoice = async (record, file) => {
        if (!access.canUploadInvoice) {
            message.error(t("invoice.noPermission"));
            return;
        }

        const action = record.hasInvoice
            ? replaceTransactionPlanInvoice
            : uploadTransactionPlanInvoice;

        await action(record.id, file);

        message.success(
            record.hasInvoice ? t("invoice.replaced.ok") : t("invoice.uploaded.ok")
        );
        reload();
    };

    const openEditModal = (record) => {
        setEditRecord(record);
        setEditModalOpen(true);
    };

    const closeEditModal = () => {
        setEditModalOpen(false);
        setEditRecord(null);
    };

    const handleDelete = async (record) => {
        if (!access.canDelete) {
            message.error(t("delete.noPermission"));
            return;
        }

        try {
            await deleteTransactionPlan(record.id);
            message.success(t("delete.done"));
            reload();
        } catch (err) {
            message.error(err?.message || t("delete.failed"));
        }
    };

    const pagination = {
        current: page,
        pageSize,
        total,
        showSizeChanger: true,
        pageSizeOptions: [10, 20, 50, 100],
        showTotal: (count, range) => `${range[0]}-${range[1]} / ${count}`,
    };

    return (
        <div className="space-y-4">
            <Card bordered={false} className="shadow-sm rounded-2xl">
                <Row gutter={[16, 16]} align="middle" justify="space-between">
                    <Col xs={24} md={14}>
                        <Title level={2} style={{ margin: 0 }}>
                            {t("plan.title")}
                        </Title>
                    </Col>

                    <Col xs={24} md="auto">
                        <Space wrap>
                            {access.canAdd && (
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => setAddModalOpen(true)}
                                >
                                    {t("action.add")}
                                </Button>
                            )}

                            <Button icon={<ReloadOutlined />} onClick={reload} loading={loading}>
                                {t("action.refresh")}
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Card>

            <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} xl={8}>
                    <Card bordered={false} className="shadow-sm rounded-2xl">
                        <div>
                            <strong>{t("plan.stat.total")}:</strong> {filterOptions.totalCount}
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} xl={8}>
                    <Card bordered={false} className="shadow-sm rounded-2xl">
                        <div>
                            <strong>{t("plan.stat.withInvoice")}:</strong> {filterOptions.withInvoiceCount}
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} xl={8}>
                    <Card bordered={false} className="shadow-sm rounded-2xl">
                        <div>
                            <strong>{t("plan.stat.filtered")}:</strong> {total}
                        </div>
                    </Card>
                </Col>
            </Row>

            <TransactionPlanFilters
                search={search}
                setSearch={setSearch}
                executionDateRange={executionDateRange}
                setExecutionDateRange={setExecutionDateRange}
                columnFilterCount={Object.keys(columnFilters || {}).length}
                totalRows={total}
                onClear={clearFilters}
            />

            <Card bordered={false} className="shadow-sm rounded-2xl">
                <TransactionPlanTable
                    rows={rows}
                    loading={loading}
                    error={error}
                    pagination={pagination}
                    onChange={handleTableChange}
                    canUploadInvoice={access.canUploadInvoice}
                    currencyOptions={filterOptions.currencies}
                    activeFilters={columnFilters}
                    canEdit={access.canEdit}
                    canDelete={access.canDelete}
                    onViewInvoice={openInvoiceModal}
                    onUploadInvoice={handleUploadInvoice}
                    onEdit={openEditModal}
                    onDelete={handleDelete}
                    onReload={reload}
                />
            </Card>

            <TransactionPlanInvoiceModal
                open={invoiceModalOpen}
                record={invoiceRecord}
                canDelete={access.canDelete}
                onClose={closeInvoiceModal}
                onDeleted={reload}
            />

            {access.canAdd && (
                <TransactionPlanAddModal
                    open={addModalOpen}
                    onClose={() => setAddModalOpen(false)}
                    onSaved={reload}
                />
            )}

            {access.canEdit && (
                <TransactionPlanEditModal
                    open={editModalOpen}
                    record={editRecord}
                    onClose={closeEditModal}
                    onSaved={reload}
                />
            )}
        </div>
    );
}

export default TransactionPlanListPage;
